/**
 * Add to Cart Widget - Production-Ready Store API Integration
 *
 * Blazing-fast, bulletproof add to cart with zero jQuery dependency.
 * Built with 2025 best practices using WooCommerce Store API exclusively.
 *
 * Performance Features:
 * - Zero jQuery (vanilla JavaScript only)
 * - Event delegation for efficiency
 * - Async/await for REST operations
 * - Modern Fetch API with retry logic
 * - WooCommerce Store API (REST endpoints only, NO AJAX)
 * - Request deduplication (prevents double-adds)
 *
 * Production Hardening:
 * - Automatic nonce refresh on authentication errors
 * - Exponential backoff retry logic
 * - Comprehensive error handling
 * - Race condition prevention
 * - User-friendly error messages
 *
 * @package Storedash_Checkout
 * @version 3.1.0
 * @updated 2025-10-14 - Production hardening with bulletproof Store API
 *
 * References:
 * - WooCommerce: /woocommerce/woocommerce - Store API cart endpoints
 * - Store API Docs: /docs/apis/store-api/resources-endpoints/cart.md
 * - MDN Web Docs: Modern JavaScript, Fetch API, retry patterns
 */

(function() {
    'use strict';

    // Configuration from WordPress
    const config = window.storedashAddToCart || {};

    // Use shared StoreAPIClient from mini-cart if available, otherwise create minimal version
    if (!window._storedashStoreAPI) {
        // This would be extracted to a shared file in production
    }

    /**
     * Add to Cart Controller
     * Handles quantity buttons and add to cart functionality
     */
    class AddToCartController {
        constructor(element) {

            if (!element) {
                return;
            }

            this.element = element;

            // Try to find wrapper inside element first
            this.wrapper = element.querySelector('.storedash-add-to-cart-wrapper');

            // Fallback: If wrapper not found, search for it directly (might be a direct child)
            if (!this.wrapper) {
                // Check if element itself is the wrapper
                if (element.classList.contains('storedash-add-to-cart-wrapper')) {
                    this.wrapper = element;
                } else {
                    // Search document for wrapper with matching product ID if available
                    const productId = element.dataset.productId || element.getAttribute('data-product-id');
                    if (productId) {
                        const wrappers = document.querySelectorAll('.storedash-add-to-cart-wrapper');
                        for (const wrapper of wrappers) {
                            if (wrapper.getAttribute('data-product-id') === productId) {
                                this.wrapper = wrapper;
                                break;
                            }
                        }
                    }
                    // Last resort: find any wrapper in the element's subtree
                    if (!this.wrapper) {
                        this.wrapper = element.querySelector('.storedash-add-to-cart-wrapper');
                    }
                }
            }

            if (!this.wrapper) {
                // Try to find wrapper anywhere in document as last resort
                const allWrappers = document.querySelectorAll('.storedash-add-to-cart-wrapper');
                if (allWrappers.length > 0) {
                    this.wrapper = allWrappers[0];
                } else {
                    return;
                }
            }

            // CRITICAL: Prevent duplicate initialization - check flag on WRAPPER not element!
            // Multiple elements can find the same wrapper (outer widget + inner wrapper selector)
            // so we must check/set the flag on the wrapper to prevent duplicate event handlers
            if (this.wrapper._storedashAddToCartInitialized) {
                return;
            }
            this.wrapper._storedashAddToCartInitialized = true;

            this.quantityWrapper = this.wrapper.querySelector('.quantity-input-wrapper');
            this.swatchesInstance = null;
            this.isLoading = false;

            // Toast settings (read once from data attributes)
            this.successAction = this.wrapper.dataset.successAction || 'open_cart';
            this.toastMessage  = this.wrapper.dataset.toastMessage || '';
            this.toastPosition = this.wrapper.dataset.toastPosition || 'bottom-center';
            this.toastDuration = parseFloat(this.wrapper.dataset.toastDuration) || 3;
            this.productName   = this.wrapper.dataset.productName || '';
            this.showAddedText   = this.wrapper.dataset.showAddedText !== '0';
            this.showLoadingDots = this.wrapper.dataset.showLoadingDots === '1';

            // FBT Integration: Store selections from storedash-wp plugin
            this.fbtSelections = null;

            // Bind methods to preserve context
            this.handleMinus = this.handleMinus.bind(this);
            this.handlePlus = this.handlePlus.bind(this);
            this.handleAddToCart = this.handleAddToCart.bind(this);

            this.init();
        }

        /**
         * Initialize controller
         */
        init() {
            this.bindEvents();
            this.bindFBTEvents();
            this.initializeSwatches();
        }

        /**
         * Initialize variation swatches if present
         */
        initializeSwatches() {
            const swatches = this.wrapper.querySelector('.storedash-variation-swatches-inline');
            
            if (swatches && typeof window.StoredashVariationSwatches !== 'undefined') {
                this.swatchesInstance = new window.StoredashVariationSwatches(swatches);
            }
        }

        /**
         * Bind events using event delegation
         */
        bindEvents() {
            
            // Use event delegation on wrapper
            this.clickHandler = (e) => {
                const minusBtn = e.target.closest('.qty-minus');
                const plusBtn = e.target.closest('.qty-plus');
                // Support both class names for button targeting
                const atcBtn = e.target.closest('.storedash-atc-button, .storedash-add-to-cart-btn');
                
                if (minusBtn) {
                    e.preventDefault();
                    this.handleMinus(minusBtn);
                } else if (plusBtn) {
                    e.preventDefault();
                    this.handlePlus(plusBtn);
                } else if (atcBtn) {
                    e.preventDefault();
                    this.handleAddToCart(atcBtn);
                }
            };

            this.wrapper.addEventListener('click', this.clickHandler);
        }

        /**
         * Bind FBT (Frequently Bought Together) event listener
         * Supports both old (storedash-wp) and new (storedash-essentials) FBT widgets
         */
        bindFBTEvents() {
            const productId = this.wrapper.dataset.productId;

            // Method 1: Listen for FBT selections updated event from OLD storedash-wp plugin
            // Store the handler as a property for cleanup in destroy()
            this._fbtSelectionsHandler = (e) => {
                // Only capture FBT selections for this specific product
                if (e.detail && e.detail.productId == productId) {
                    this.fbtSelections = e.detail;

                    // Debug logging
                    if (config.debug && typeof console !== 'undefined') {
                        console.log('[StoreDash Add to Cart] FBT selections captured (legacy):', {
                            productId: productId,
                            fbtCount: e.detail.parsedItems?.length || 0,
                            fbtItems: e.detail.parsedItems
                        });
                    }
                }
            };

            document.addEventListener('storedash_fbt_selections_updated', this._fbtSelectionsHandler);
        }

        /**
         * Find FBT widget on the page (NEW essentials plugin FBT widget)
         * @param {number} productId
         * @returns {Object|null} FBT widget instance or null
         */
        findFBTWidget(productId) {
            if (config.debug && typeof console !== 'undefined') {
                console.log('[StoreDash Add to Cart] Looking for FBT widget for product:', productId);
            }

            const containers = document.querySelectorAll('.storedash-fbt-widget-container');

            if (config.debug && typeof console !== 'undefined') {
                console.log('[StoreDash Add to Cart] Found FBT containers:', containers.length);
            }

            for (const container of containers) {
                const containerProductId = container.dataset.productId;

                if (config.debug && typeof console !== 'undefined') {
                    console.log('[StoreDash Add to Cart] Checking container with product ID:', containerProductId, 'against:', productId);
                }

                // Use strict comparison with type coercion to handle string/number mismatches
                if (String(containerProductId) === String(productId)) {
                    // Check if widget has exposed API
                    const fbtWidget = container._fbtWidgetInstance;

                    if (config.debug && typeof console !== 'undefined') {
                        console.log('[StoreDash Add to Cart] FBT widget instance found:', !!fbtWidget);
                        console.log('[StoreDash Add to Cart] getSelectedProducts available:', !!(fbtWidget && typeof fbtWidget.getSelectedProducts === 'function'));
                    }

                    if (fbtWidget && typeof fbtWidget.getSelectedProducts === 'function') {
                        return fbtWidget;
                    }
                }
            }

            if (config.debug && typeof console !== 'undefined') {
                console.log('[StoreDash Add to Cart] No matching FBT widget found');
            }

            return null;
        }

        /**
         * Parse FBT string from hidden input (fallback method)
         * Format: "id/key/qty/encoded_attrs,id2/key2/qty2/encoded_attrs2"
         * @param {string} fbtString
         * @returns {Object|null}
         */
        parseFBTString(fbtString) {
            if (!fbtString || fbtString.trim() === '') {
                return null;
            }

            try {
                const items = fbtString.split(',');
                const parsedItems = items.map(item => {
                    if (!item) return null;
                    const parts = item.split('/');
                    if (parts.length < 3) return null;

                    let attrs = {};
                    if (parts[3]) {
                        try {
                            attrs = JSON.parse(decodeURIComponent(parts[3]));
                        } catch (e) {
                            // Invalid JSON, use empty object
                        }
                    }

                    return {
                        productId: parts[0],
                        key: parts[1],
                        quantity: parseInt(parts[2], 10) || 1,
                        attrs: attrs
                    };
                }).filter(item => item !== null);

                if (parsedItems.length === 0) {
                    return null;
                }

                return {
                    fbtIds: fbtString,
                    parsedItems: parsedItems
                };
            } catch (error) {
                if (config.debug && typeof console !== 'undefined') {
                    console.error('[StoreDash Add to Cart] Failed to parse FBT string:', error);
                }
                return null;
            }
        }

        /**
         * Handle minus button click
         */
        handleMinus(button) {
            const wrapper = button.closest('.quantity-input-wrapper');
            const input = wrapper.querySelector('.qty-input');
            const min = parseFloat(input.getAttribute('min')) || 1;
            const val = parseFloat(input.value) || min;
            
            if (val > min) {
                input.value = val - 1;
            }
        }

        /**
         * Handle plus button click
         */
        handlePlus(button) {
            const wrapper = button.closest('.quantity-input-wrapper');
            const input = wrapper.querySelector('.qty-input');
            const min = parseFloat(input.getAttribute('min')) || 1;
            const max = parseFloat(input.getAttribute('max')) || 999;
            const val = parseFloat(input.value) || min;
            
            if (val < max) {
                input.value = val + 1;
            }
        }

        /**
         * Handle add to cart button click using Store API
         *
         * PERFORMANCE: Uses optimistic UI pattern for instant perceived response.
         * Shows "Added!" and bumps cart badge at ~250ms instead of waiting for
         * the full Store API round trip (~1-2s). Rolls back on failure.
         */
        async handleAddToCart(button) {
            if (!button || button.disabled || button.classList.contains('disabled') || button.classList.contains('out-of-stock')) {
                return;
            }

            if (this.isLoading || button.classList.contains('adding')) {
                return;
            }

            const productId = this.wrapper.dataset.productId;
            const qtyInput = this.wrapper.querySelector('.qty-input');
            const quantity = qtyInput ? parseInt(qtyInput.value) : 1;

            if (!productId) {
                return;
            }

            // Check if this is a variable product
            const isVariable = button.classList.contains('variable-product');
            let variationId = 0;

            if (isVariable) {
                const variationInput = this.wrapper.querySelector('.variation_id');
                variationId = variationInput ? parseInt(variationInput.value) : 0;

                if (!variationId || variationId === 0) {
                    alert(config.i18n?.selectOptions || 'Please select product options');
                    return;
                }
            }

            // Lock against double-click (stays true until API completes)
            this.isLoading = true;

            // Capture original state for rollback
            const textEl = button.querySelector('span') || button;
            const originalText = textEl.textContent;
            const successText = this.wrapper.dataset.successText || config.i18n?.added || 'Added!';

            // PHASE 1: Optimistic UI — instant feedback
            button.classList.remove('added');
            button.classList.add('adding');
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');

            // Show loading dots if enabled
            if (this.showLoadingDots) {
                textEl.dataset.originalText = textEl.textContent;
                textEl.innerHTML = '<span class="storedash-loading-dots"><span></span><span></span><span></span></span>';
            }

            // Show "Added!" after a brief "Adding..." flash (250ms micro-interaction)
            const optimisticTimer = setTimeout(() => {
                button.classList.remove('adding');
                button.classList.add('added');
                if (this.showAddedText) {
                    textEl.textContent = successText;
                } else if (this.showLoadingDots) {
                    textEl.textContent = textEl.dataset.originalText || originalText;
                }
                button.disabled = false;
                button.removeAttribute('aria-disabled');
            }, 250);

            // Optimistically bump mini-cart badge count
            document.body.dispatchEvent(new CustomEvent('storedash_optimistic_cart_add', {
                detail: { quantity }
            }));

            // PHASE 2: Real API call (runs in background while user sees "Added!")
            try {
                const productIdToAdd = isVariable && variationId ? variationId : productId;

                // FBT Integration: Check for FBT selections
                let fbtData = this.fbtSelections;

                // Method 1: Check for NEW FBT widget (storedash-essentials)
                if (!fbtData) {
                    const fbtWidget = this.findFBTWidget(productId);
                    if (fbtWidget) {
                        const selectedProducts = fbtWidget.getSelectedProducts();
                        if (selectedProducts && selectedProducts.length > 0) {
                            fbtData = { parsedItems: selectedProducts };
                        }
                    }
                }

                // Fallback Method 2: Try reading from hidden input (OLD storedash-wp plugin)
                if (!fbtData) {
                    const hiddenInput = document.querySelector(`.storedash-fbt-ids-${productId}`);
                    if (hiddenInput && hiddenInput.value) {
                        fbtData = this.parseFBTString(hiddenInput.value);
                    }
                }

                const hasFBTProducts = fbtData && fbtData.parsedItems && fbtData.parsedItems.length > 0;

                const data = hasFBTProducts
                    ? await this.addFBTBundle(productIdToAdd, quantity, fbtData.parsedItems)
                    : await this.addToCartWithRetry(productIdToAdd, quantity);

                // API confirmed success — ensure optimistic state is showing
                clearTimeout(optimisticTimer);
                button.classList.remove('adding');
                button.classList.add('added');
                if (this.showAddedText) {
                    textEl.textContent = successText;
                } else {
                    textEl.textContent = originalText;
                }
                button.disabled = false;
                button.removeAttribute('aria-disabled');

                // Dispatch events with real cart data (mini-cart syncs actual items/totals)
                this.triggerAddToCartEvents(data, button, productIdToAdd);

                // Show success toast if configured
                if (this.successAction === 'show_toast') {
                    const messageTemplate = this.toastMessage || '{product_name} added to cart';
                    const messageText = messageTemplate.replace(/\{product_name\}/g, this.productName);
                    StoredashToast.success(messageText, {
                        position: this.toastPosition,
                        duration: this.toastDuration
                    });
                }

                // Remove any "View cart" links that WooCommerce might add
                setTimeout(() => {
                    document.querySelectorAll('.added_to_cart.wc-forward, .woocommerce-message')
                        .forEach(link => link.remove());
                }, 100);

                // Reset button after 2 seconds
                setTimeout(() => {
                    button.classList.remove('added');
                    textEl.textContent = originalText;
                }, 2000);

            } catch (error) {
                // ROLLBACK: Revert all optimistic updates
                clearTimeout(optimisticTimer);
                button.classList.remove('adding', 'added');
                textEl.textContent = originalText;

                // Rollback optimistic cart badge count
                document.body.dispatchEvent(new CustomEvent('storedash_optimistic_cart_rollback', {
                    detail: { quantity }
                }));

                if (this.isOutOfStockError(error)) {
                    this.markButtonOutOfStock(button);
                } else {
                    button.disabled = false;
                    button.removeAttribute('aria-disabled');
                    button.classList.remove('disabled');
                }

                StoredashToast.errorFromResponse(error, { product_name: this.productName });
            } finally {
                this.isLoading = false;
            }
        }

        /**
         * Add to cart with automatic retry logic
         * @param {number} productId
         * @param {number} quantity
         * @param {number} retryCount
         * @returns {Promise<Object>} Normalized cart data
         */
        async addToCartWithRetry(productId, quantity, retryCount = 0) {
            const maxRetries = 3;
            const storeApiUrl = (config.storeApiUrl || '/wp-json/wc/store/v1/').replace(/\/$/, '') + '/cart/add-item';

            try {
                const response = await fetch(storeApiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Nonce': config.storeApiNonce || '',
                    },
                    body: JSON.stringify({
                        id: parseInt(productId),
                        quantity: parseInt(quantity)
                    }),
                    credentials: 'same-origin',
                });

                // Handle authentication errors (stale nonce)
                if (response.status === 401 || response.status === 403) {

                    if (retryCount === 0) {
                        // Try refreshing the nonce once
                        const refreshed = await this.tryRefreshNonce();
                        if (refreshed) {
                            return this.addToCartWithRetry(productId, quantity, retryCount + 1);
                        }
                    }

                    const authError = new Error('Your session has expired. Please refresh the page.');
                    authError.code = 'woocommerce_rest_authentication_error';
                    authError.status = response.status;
                    throw authError;
                }

                // Parse response
                const data = await response.json();

                // Handle other HTTP errors
                if (!response.ok) {
                    const errorMessage = data.message || data.code || 'Failed to add product to cart';
                    const error = new Error(errorMessage);
                    error.status = response.status;
                    if (data && typeof data.code === 'string') {
                        error.code = data.code;
                    }
                    if (data && data.data) {
                        error.data = data.data;
                    }
                    throw error;
                }

                // Normalize response (handle field name inconsistencies)
                return this.normalizeCartResponse(data);

            } catch (error) {

                // Retry on network errors
                if (retryCount < maxRetries && this.isRetryableError(error)) {
                    const delay = 500 * Math.pow(2, retryCount); // Exponential backoff

                    await this.sleep(delay);
                    return this.addToCartWithRetry(productId, quantity, retryCount + 1);
                }

                throw error;
            }
        }

        /**
         * Add multiple items to cart using Store API batch endpoint
         * @param {Array<{id:number, quantity:number}>} items
         * @returns {Promise<Object>} Normalized cart data
         */
        async addToCartBatch(items, retryCount = 0) {
            const storeApiBase = (config.storeApiUrl || '/wp-json/wc/store/v1/').replace(/\/$/, '');
            const batchUrl = `${storeApiBase}/batch`;

            const requests = items.map((item) => ({
                path: '/wc/store/v1/cart/add-item',
                method: 'POST',
                cache: 'no-store',
                body: {
                    id: parseInt(item.id),
                    quantity: parseInt(item.quantity) || 1
                },
                headers: {
                    Nonce: config.storeApiNonce || ''
                }
            }));

            const response = await fetch(batchUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Nonce': config.storeApiNonce || ''
                },
                body: JSON.stringify({ requests }),
                credentials: 'same-origin'
            });

            if (response.status === 401 || response.status === 403) {
                if (retryCount === 0) {
                    const refreshed = await this.tryRefreshNonce();
                    if (refreshed) {
                        return this.addToCartBatch(items, retryCount + 1);
                    }
                }

                const authError = new Error('Your session has expired. Please refresh the page.');
                authError.code = 'woocommerce_rest_authentication_error';
                authError.status = response.status;
                throw authError;
            }

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = data?.message || data?.code || 'Failed to add products to cart';
                const error = new Error(errorMessage);
                error.status = response.status;
                if (data && typeof data.code === 'string') {
                    error.code = data.code;
                }
                throw error;
            }

            const responses = data?.responses || data?.response || [];
            if (!Array.isArray(responses) || responses.length === 0) {
                throw new Error('Batch response missing cart data.');
            }

            // Find the last successful cart response
            const successful = responses.filter((entry) => {
                const status = entry?.status || entry?.code || 0;
                return status >= 200 && status < 300 && entry?.body;
            });

            if (successful.length === 0) {
                const firstError = responses.find((entry) => (entry?.status || 0) >= 400);
                const errorMessage = firstError?.body?.message || 'Failed to add products to cart.';
                const error = new Error(errorMessage);
                error.status = firstError?.status || 400;
                if (firstError?.body?.code) {
                    error.code = firstError.body.code;
                }
                throw error;
            }

            const lastCart = successful[successful.length - 1].body;
            return this.normalizeCartResponse(lastCart);
        }

        /**
         * Add main product + FBT companions via custom REST endpoint.
         * Discount is applied server-side from product meta — client cannot manipulate it.
         *
         * @param {number} productId
         * @param {number} quantity
         * @param {Array}  fbtItems - from getSelectedProducts()
         * @returns {Promise<Object>} Normalized cart-like response
         */
        async addFBTBundle(productId, quantity, fbtItems) {
            const bundleUrl = (config.siteUrl || '') + '/wp-json/storedash-essentials/v1/cart/add-fbt-bundle';

            const response = await fetch(bundleUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': config.wpNonce || '',
                },
                body: JSON.stringify({
                    product_id: parseInt(productId),
                    quantity:   parseInt(quantity) || 1,
                    fbt_items:  fbtItems.map(item => ({
                        id:       parseInt(item.productId),
                        quantity: parseInt(item.quantity) || 1,
                    })),
                }),
                credentials: 'same-origin',
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                const error = new Error(err.message || 'Failed to add FBT bundle');
                error.status = response.status;
                if (err.code) error.code = err.code;
                throw error;
            }

            const data = await response.json();

            return {
                items_count: data.items_count || 0,
                items: [],
                totals: {},
                _raw: data,
            };
        }

        /**
         * Normalize Store API cart response
         * @param {Object} data
         * @returns {Object}
         */
        normalizeCartResponse(data) {
            const itemCount = data.items_count ?? data.item_count ?? data.items?.length ?? 0;

            return {
                items_count: itemCount,
                items: data.items || [],
                totals: data.totals || {},
                _raw: data
            };
        }

        /**
         * Check if error is retryable
         * @param {Error} error
         * @returns {boolean}
         */
        isRetryableError(error) {
            return error.message.includes('Failed to fetch') ||
                   error.message.includes('NetworkError') ||
                   error.message.includes('timeout') ||
                   error.message.includes('HTTP 5');
        }

        /**
         * Determine if error indicates stock unavailability
         * @param {Error} error
         * @returns {boolean}
         */
        isOutOfStockError(error) {
            const message = (error?.message || '').toLowerCase();
            const code = (error?.code || '').toLowerCase();

            if (!message && !code) {
                return false;
            }

            const indicators = [
                'out of stock',
                'stock unavailable',
                'no longer available',
                'product_stock',
                'out_of_stock',
                'stock_exceeded',
            ];

            return indicators.some(keyword => message.includes(keyword) || code.includes(keyword));
        }

        /**
         * Mark button as out of stock to prevent repeated attempts
         * @param {HTMLButtonElement} button
         * @param {string} [label]
         */
        markButtonOutOfStock(button, label) {
            if (!button) return;

            button.classList.remove('adding', 'added');
            button.classList.add('disabled', 'out-of-stock');
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
        }

        /**
         * Try to refresh the Store API nonce
         * @returns {Promise<boolean>}
         */
        async tryRefreshNonce() {
            try {
                const response = await fetch('/wp-json/wc/store/v1/cart', {
                    method: 'GET',
                    credentials: 'same-origin',
                    headers: { 'Cache-Control': 'no-cache' }
                });

                const newNonce = response.headers.get('Nonce') || response.headers.get('X-WC-Store-API-Nonce');

                if (newNonce && newNonce !== config.storeApiNonce) {
                    config.storeApiNonce = newNonce;
                    return true;
                }

                return false;
            } catch (error) {
                return false;
            }
        }

        /**
         * Sleep helper
         * @param {number} ms
         * @returns {Promise<void>}
         */
        sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        /**
         * Trigger WooCommerce events for cart updates (Store API format)
         * 
         * Store API returns different data structure than legacy AJAX:
         * - data.items: Array of cart items
         * - data.totals: Cart totals object
         * - data.items_count: Total number of items
         * 
         * @param {Object} data - Store API response data
         * @param {HTMLElement} button - The add to cart button
         * @param {number} productId - Product ID that was added
         */
        triggerAddToCartEvents(data, button, productId) {
            // Modern CustomEvent with Store API data structure
            const eventDetail = {
                detail: {
                    // Store API response
                    cart: data,
                    items: data.items || [],
                    totals: data.totals || {},
                    itemCount: data.items_count || 0,

                    // Additional context
                    button: button,
                    productId: productId,

                    // CRITICAL: Flag to identify events from Storedash widgets
                    // This prevents the mini cart from intercepting WoodMart/other theme events
                    fromStoredash: true,

                    // When toast mode is active, suppress mini-cart sidebar open
                    suppressMiniCart: this.successAction === 'show_toast',
                },
            };

            // Dispatch modern native events
            document.body.dispatchEvent(new CustomEvent('added_to_cart', eventDetail));
            document.body.dispatchEvent(new CustomEvent('wc-blocks_added_to_cart', eventDetail));
            document.body.dispatchEvent(new CustomEvent('storedash_added_to_cart', eventDetail));

            // NOTE: We do NOT trigger wc_fragments_refresh with Store API
            // Store API uses a different pattern - the mini cart listens to events above
            // and updates itself via Store API, not via HTML fragments
        }

        /**
         * Destroy instance and cleanup
         */
        destroy() {
            if (this.clickHandler && this.wrapper) {
                this.wrapper.removeEventListener('click', this.clickHandler);
            }

            // Clean up FBT event listener to prevent memory leak
            if (this._fbtSelectionsHandler) {
                document.removeEventListener('storedash_fbt_selections_updated', this._fbtSelectionsHandler);
                this._fbtSelectionsHandler = null;
            }

            if (this.swatchesInstance && typeof this.swatchesInstance.destroy === 'function') {
                this.swatchesInstance.destroy();
            }
        }
    }

    /**
     * Initialize widgets on Elementor frontend
     *
     * IMPORTANT: Only registers the Elementor hook here.
     * The hook will fire for each widget when Elementor renders it.
     * Do NOT also initialize existing widgets here - that causes double initialization.
     */
    function initElementorWidget() {
        if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {

            elementorFrontend.hooks.addAction('frontend/element_ready/storedash-add-to-cart.default', function($scope) {
                // $scope is a jQuery object in Elementor context, get native element
                const element = $scope[0] || $scope;
                new AddToCartController(element);
            });

            // NOTE: Do NOT initialize existing widgets here!
            // The Elementor hook above will fire for each widget.
            // Initializing here too causes double event handlers (quantity +2 bug).
        }
        // If Elementor hooks not available, the fallback in initOnDOMReady() handles it
    }

    // Track if Elementor widget has been initialized to prevent duplicates
    let elementorWidgetInitialized = false;

    // Always wait for Elementor init event to ensure hooks are ready
    // This prevents the "hooks not available" error
    if (typeof elementorFrontend !== 'undefined') {
        // elementorFrontend exists, check if hooks are already available
        if (elementorFrontend.hooks) {
            initElementorWidget();
            elementorWidgetInitialized = true;
        } else {
            // hooks not ready yet, wait for init event
            window.addEventListener('elementor/frontend/init', () => {
                if (!elementorWidgetInitialized) {
                    initElementorWidget();
                    elementorWidgetInitialized = true;
                }
            });
        }
    } else {
        // Wait for Elementor to load
        window.addEventListener('elementor/frontend/init', () => {
            if (!elementorWidgetInitialized) {
                initElementorWidget();
                elementorWidgetInitialized = true;
            }
        });
    }

    /**
     * Initialize widgets on regular pages (non-Elementor) or as fallback
     */
    function initAddToCartWidgets() {
        const widgets = document.querySelectorAll('.elementor-widget-storedash-add-to-cart, .brxe-storedash-add-to-cart, .storedash-add-to-cart-wrapper');
        widgets.forEach((widget, index) => {
            // Check if already initialized
            if (!widget._storedashAddToCartInitialized) {
                new AddToCartController(widget);
            }
        });

        if (widgets.length === 0) {
        }
    }

    /**
     * Initialize when DOM is ready - ONLY for non-Elementor pages
     *
     * IMPORTANT: Do NOT run this as a fallback when Elementor is present!
     * The Elementor hook system handles initialization for Elementor pages.
     * Running both causes double event handlers (quantity +2 bug).
     */
    function initOnDOMReady() {
        const init = () => {
            // Only run fallback if Elementor is NOT present
            // If Elementor IS present, trust the hook system to handle it
            if (typeof elementorFrontend === 'undefined' || !elementorFrontend.hooks) {
                initAddToCartWidgets();
            }
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            // DOM already loaded
            init();
        }
    }

    // Initialize on DOM ready (only runs for non-Elementor pages)
    initOnDOMReady();

    // Expose controller for external use
    window.StoredashAddToCartController = AddToCartController;

    // Bricks Builder re-init function (called by Bricks when element renders/re-renders in editor)
    window.storedashBricksAddToCart = function(element) {
        if (!element) return;
        const existing = element._storedashAddToCartController;
        if (existing && typeof existing.destroy === 'function') {
            existing.destroy();
        }
        new AddToCartController(element);
    };

})();
